/**
 * Flutter-faithful form primitives for the patient Add/Edit screens.
 *
 * These mirror the Flutter building blocks rather than the generic shadcn
 * patterns so the React Personal Details tab matches the Flutter layout
 * pixel-for-intent, rendered with Signal DS tokens (no hardcoded hex):
 *
 *   - `FormSection`  → Flutter `FormView` (`form_view_widget.dart`):
 *       the section label ABOVE a white rounded card (the approved design's FormSection look). `bare`
 *       drops the card chrome (used by "I AM ADDING" / "ID CARD" which
 *       render their content directly on the page background).
 *   - `FieldLabel`   → Flutter `defaultFieldLabelStyle` field label.
 *   - `PillPicker`   → Flutter `FieldType.SingleTagPicker` (gender, consent,
 *       importance, marital, residency, ID type, interpreter): rounded-full
 *       chips, grey-100 / brand-primary.
 *   - `PatientTypeCards` → Flutter "I AM ADDING" / "PATIENT IS" big
 *       MINOR / ADULT cards (160×48, icon chip, 2px brand ring when active).
 *   - `FIELD_INPUT_CLASS` → filled grey text-input shape matching Flutter
 *       `textFieldBGcolor`.
 *
 * Tokens used: bg-surface-card, bg-grey-100/200, text-brand-primary,
 * text-text-{primary,muted,hint}, ring-brand-primary, border-border-default,
 * rounded-{full,lg,xl}, status-error. All Signal DS Tailwind tokens.
 */

import type { ReactNode } from 'react';

/* FIELD_INPUT_CLASS was DELETED 2026-09-19.
 *
 * It hand-wrote a text field's whole chrome - `h-10 px-3 rounded-md border border-border-default
 * bg-surface-card ... focus ring` - and was applied to 21 `<Input>` sites on the patient forms.
 * Those are now `<TextField size="compact">`, the design system's own 36px field, so the
 * constant lost every caller. It is not re-exported as a shim: a local copy of a component's
 * chrome is exactly the drift this migration exists to end.
 *
 * NOTE for whoever migrates `<Select>`: education-section.tsx still styles a select BY HAND to
 * match this shape. Deleting this does not fix that site; it moves when Select does. */

/* FieldLabel was GRADUATED to the design system on 2026-09-20 and DELETED here.
 *
 * It had 139 call sites, and a further 51 raw <Label> elements spelled the identical
 * recipe inline. ~190 uses of one unnamed recipe is a component the design system was
 * missing. Import it from '@scripsteam/scrips-signal-ds'.
 *
 * The size literal went with it: this wrote `text-[11px]`, and `--text-xs` IS 11px. */

/**
 * FormView card — section label above a white rounded card.
 * `bare` renders the children directly on the page background (no card),
 * matching Flutter sections that aren't wrapped in a `FormView`
 * ("I AM ADDING", "ID CARD", "PATIENT STATUS", "PATIENT IS").
 */
export function FormSection({
  title,
  required,
  subtitle,
  bare = false,
  children,
  dataTour,
}: {
  title: string;
  required?: boolean;
  subtitle?: string;
  bare?: boolean;
  children: ReactNode;
  /** Optional `data-tour` anchor for the release-tour framework. */
  dataTour?: string;
}) {
  // The approved design's FormSection, unconditional: the section name in the label role (sentence case, medium,
  // body ink) over an optional hint, then the one card (12 corners, hairline, white, no shadow, 24 padding, the fields
  // 24 apart). `bare` drops the card. Look: showcase-patient.css `/* form sections */`.
  return (
    <section data-slot="form-section" data-tour={dataTour}>
      <h2 data-slot="form-section-title">
        {title}
        {required ? <span data-slot="form-section-star" aria-hidden>*</span> : null}
      </h2>
      {subtitle ? <p data-slot="form-section-hint">{subtitle}</p> : null}
      <div data-slot={bare ? 'form-section-bare' : 'form-section-card'}>{children}</div>
    </section>
  );
}

export interface PillOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Single-select pill chooser — Flutter `FieldType.SingleTagPicker`.
 * `allowUnselect` (default true) mirrors Flutter: tapping the active pill
 * clears it, EXCEPT for consent (`allowUnselect: false`).
 */
export function PillPicker<T extends string>({
  options,
  value,
  onChange,
  allowUnselect = true,
  disabled = false,
  'aria-invalid': ariaInvalid,
}: {
  options: ReadonlyArray<PillOption<T>>;
  value: T | null | undefined;
  onChange: (next: T | null) => void;
  allowUnselect?: boolean;
  disabled?: boolean;
  'aria-invalid'?: boolean;
}) {
  return (
    // The design's pill chooser (PaFieldGrid `pills`): hairline pills, white, label type; the chosen one on the brand
    // edge with the active-blue tint. Look: showcase-patient.css `/* field pills */`. Same click / unselect rule.
    <div data-slot="field-pills" aria-invalid={ariaInvalid}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-slot="field-pill"
            data-active={active ? 'true' : undefined}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(active && allowUnselect ? null : opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * MINOR / ADULT selectable cards — Flutter "I AM ADDING" (add) and
 * "PATIENT IS" (edit). `value` is `isAdult`; selecting a card calls
 * `onChange(isAdult)`. Selected card gets a 2px brand ring (Flutter
 * `Border.all(color: enabledBtnBGColor, width: 2)`).
 */
export function PatientTypeCards({
  value,
  onChange,
  disabled = false,
}: {
  /** Current `isAdult`. */
  value: boolean;
  onChange: (isAdult: boolean) => void;
  disabled?: boolean;
}) {
  const cards = [
    { isAdult: false, title: 'Minor', sub: '0-18 years old' },
    { isAdult: true, title: 'Adult', sub: '>18 years old' },
  ] as const;

  // The design draws the patient type as its pill chooser (new-patient-1-details.html `I am adding`, PaFieldGrid `pills`):
  // the same two choices and handler; each pill's age range is its tooltip and accessible description.
  return (
    <div data-slot="field-pills">
      {cards.map((c) => {
        const active = value === c.isAdult;
        return (
          <button
            key={c.title}
            type="button"
            data-slot="field-pill"
            data-active={active ? 'true' : undefined}
            aria-pressed={active}
            title={c.sub}
            aria-description={c.sub}
            disabled={disabled}
            onClick={() => onChange(c.isAdult)}
          >
            {c.title}
          </button>
        );
      })}
    </div>
  );
}
